/*
 * RecognitionModal: Anlagenerkennung eines Geräts (docs/anlagenerkennung.md).
 *
 * Vier Schritte, immer nur einer sichtbar (Patrick, 16.10.2026: "völlig
 * unnatürlich, mir scheint er wendet es sofort an"):
 *   1. Set wählen      nichts passiert, bis "Konfiguration lesen" gedrückt wird
 *   2. Lesen           nur Fortschritt, je Prüfpunkt "wartet" oder "gelesen"
 *   3. Ergebnis prüfen Erkanntes, Bausteine; "noch nichts geändert"; einziger
 *                      Ort, an dem übernommen wird
 *   4. Fertig          was angewendet wurde
 * Öffnet man das Fenster bei einer schon bestätigten Anlage, steht dort der
 * Stand mit "Neu erkennen", kein Übernehmen-Knopf.
 */
import { useEffect, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { showToast } from './Toaster';
import './hv-modal.css';
import './RecognitionModal.css';

interface Props {
  plantId: string;
  deviceId: string;
  deviceName: string;
  onClose(): void;
  onApplied(): void;
}

interface ProfileRef { id: string; version: number; name: string; description: string | null }
interface ProfileVersion {
  key: string; version: number; name: string; description: string | null; protocols: string[]; builtIn: boolean;
  note: string | null; probes: number; modules: number; leaves: number;
}
interface Probe { key: string; name: string; address: string; value: unknown; text: string | null; at: string | null; fresh: boolean }
interface Feature { key: string; label: string; value: string; text: string | null; uncertain: boolean }
interface Module {
  key: string; label: string; state: 'ja' | 'nein' | 'unbekannt'; included: boolean;
  override: boolean | null; rule: string | null; leaves: number;
}
interface Match { templateId: string; version: number; name: string; kind: 'features' | 'structure' }
interface State {
  state: 'none' | 'waiting' | 'recognized' | 'confirmed' | 'drift';
  profiles: ProfileRef[];
  profile: ProfileRef | null;
  startedAt: string | null;
  waitUntil: string | null;
  probes: Probe[];
  features: Feature[];
  proposal: { name: string; modules: Module[]; leafCount: number; match: Match | null } | null;
  confirmedAt: string | null;
  confirmedBy: string | null;
  drift: { key: string; label: string; confirmed: string; now: string }[];
  hmiNote?: string | null;
  confirmedModules?: string[] | null;
}

type Step = 'choose' | 'reading' | 'review' | 'done' | 'overview';
const STEPS: { id: Step; label: string }[] = [
  { id: 'choose', label: 'Set wählen' },
  { id: 'reading', label: 'Konfiguration lesen' },
  { id: 'review', label: 'Ergebnis prüfen' },
  { id: 'done', label: 'Übernommen' },
];

const hasValue = (p: Probe) => p.value !== null && p.value !== undefined;

export function RecognitionModal({ plantId, deviceId, deviceName, onClose, onApplied }: Props) {
  const base = `/api/plants/${plantId}/devices/${deviceId}/recognition`;
  const [state, setState] = useState<State | null>(null);
  const [step, setStep] = useState<Step | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState<ProfileVersion[]>([]);
  const [choice, setChoice] = useState('');
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [orphans, setOrphans] = useState<string | null>(null);
  const [composeHmi, setComposeHmi] = useState(true);
  const [now, setNow] = useState(Date.now());

  const load = () =>
    api.get<State>(base).then((s) => { setState(s); setError(null); return s; })
      .catch((e) => { setError(errorText(e)); return null; });

  // Beim Öffnen: Stand holen. Nichts starten, und nie in einen alten Lauf
  // springen: nur ein laufendes Lesen wird wieder aufgenommen; ein Ergebnis
  // nur, wenn es keine 15 Minuten alt ist. Sonst Auswahl bzw. Stand.
  useEffect(() => {
    load().then((s) => {
      if (!s) return;
      // Ein Ergebnis wird nur in der Sitzung übernommen, die gelesen hat.
      setStep(s.state === 'waiting' ? 'reading' : s.confirmedAt ? 'overview' : 'choose');
    });
    api.get<ProfileVersion[]>('/api/recognition/profiles').then(setVersions).catch(() => setVersions([]));
  }, [plantId, deviceId]);

  // Beim Lesen alle 2 s nachsehen; ist alles da oder die Zeit um, weiter zum Ergebnis.
  useEffect(() => {
    if (step !== 'reading') return;
    const t = setInterval(async () => {
      const s = await load();
      if (s && s.state !== 'waiting') setStep('review');
    }, 2000);
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [step]);

  // Vorauswahl: neueste Fassung des passenden Sets.
  const protocolVersions = versions.filter((v) => state?.profiles.some((p) => p.id === v.key));
  useEffect(() => {
    if (choice) return;
    const latest = [...protocolVersions].sort((a, b) => b.version - a.version)[0];
    if (latest) setChoice(`${latest.key}@${latest.version}`);
    else if (state?.profiles[0]) setChoice(`${state.profiles[0].id}@${state.profiles[0].version}`);
  }, [state, versions]);
  const chosen = protocolVersions.find((v) => `${v.key}@${v.version}` === choice);

  const start = async () => {
    const [profileId, v] = choice.split('@');
    setBusy(true);
    try {
      const s = await api.post<State>(`${base}/start`, { profileId: profileId || null, profileVersion: v ? Number(v) : null });
      setState(s);
      setOverrides({});
      setOrphans(null);
      setStep('reading');
    } catch (e) {
      showToast({ tone: 'error', title: 'Lesen nicht gestartet', message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (acceptOrphanedBindings = false) => {
    setBusy(true);
    try {
      const next = await api.post<State>(`${base}/confirm`, { overrides, acceptOrphanedBindings, composeHmi });
      setState(next);
      setOrphans(null);
      setStep('done');
      onApplied();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setOrphans(errorText(e));
      else showToast({ tone: 'error', title: 'Nicht übernommen', message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  // Nur die HMI neu zusammensetzen, ohne Schema und Vorlage anzufassen: etwa
  // nach einem neuen Erkennungsset oder nachdem Punkte geloescht wurden.
  const rebuildHmi = async () => {
    if (!window.confirm('HMI aus den bestätigten Bausteinen neu zusammensetzen? Von Hand ergänzte Widgets bleiben, '
      + 'die erzeugten werden ersetzt. Das Panel holt die neue Fassung binnen etwa 90 Sekunden.')) return;
    setBusy(true);
    try {
      const next = await api.post<State>(`${base}/hmi`, {});
      setState(next);
      // Der Server antwortet auch bei einer abgelehnten HMI mit 200 und nennt
      // den Grund nur im Hinweis (Kutiak, 18.10.2026).
      const failed = (next.hmiNote ?? '').startsWith('HMI nicht');
      showToast({ tone: failed ? 'error' : 'success', title: failed ? 'HMI nicht zusammengesetzt' : 'HMI neu zusammengesetzt',
        message: next.hmiNote ?? undefined });
    } catch (e) {
      showToast({ tone: 'error', title: 'HMI nicht zusammengesetzt', message: errorText(e) });
    } finally {
      setBusy(false);
    }
  };

  const probes = state?.probes ?? [];
  const fresh = probes.filter((p) => p.fresh).length;
  const total = probes.length;
  const secondsLeft = state?.waitUntil ? Math.max(0, Math.round((new Date(state.waitUntil).getTime() - now) / 1000)) : 0;
  const modules = state?.proposal?.modules ?? [];
  const included = (m: Module) => overrides[m.key] ?? m.included;
  const found = state?.features.filter((f) => !['aus', 'unbekannt'].includes(f.value)) ?? [];
  const absent = state?.features.filter((f) => f.value === 'aus') ?? [];
  const unread = state?.features.filter((f) => f.value === 'unbekannt') ?? [];
  const stepIndex = STEPS.findIndex((s) => s.id === step);
  // Was bisher bestätigt war und jetzt wegfiele: muss vor dem Übernehmen
  // unübersehbar sein (Labor, 16.10.2026: Trinkwasser fiel weg, weil sein
  // Teilschema 0 meldet, und die Rückfrage wurde weggeklickt).
  const lost = modules.filter((m) => (state?.confirmedModules ?? []).includes(m.key) && !included(m));

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !busy && onClose()}>
      <div class="hv-modal hv-modal--wide hv-recog" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Anlage erkennen · {deviceName}</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen" disabled={busy}>×</button>
        </div>
        <div class="hv-modal__body hv-recog__body">
          {error && <p class="hv-modal__error">{error}</p>}
          {!step && !error && <p class="muted">Lade…</p>}

          {step && step !== 'overview' && (
            <ol class="hv-recog__steps" aria-label="Ablauf">
              {STEPS.map((s, i) => (
                <li key={s.id} class={i < stepIndex ? 'is-done' : i === stepIndex ? 'is-now' : ''}>
                  <span class="hv-recog__step-dot">{i < stepIndex ? '✓' : i + 1}</span>{s.label}
                </li>
              ))}
            </ol>
          )}

          {/* ------------------------------------------------ Stand */}
          {step === 'overview' && state && (
            <section class="hv-recog__panel">
              <h3>Bestätigt {state.confirmedAt ? new Date(state.confirmedAt).toLocaleString('de-AT') : ''}{state.confirmedBy ? ` von ${state.confirmedBy}` : ''}</h3>
              <p>Aufgenommen: {modules.filter((m) => m.included).map((m) => m.label).join(', ') || '–'}.</p>
              {state.state === 'drift' && (
                <div class="hv-recog__drift">
                  <b>Der Regler meldet inzwischen etwas anderes:</b>
                  <ul>{state.drift.map((d) => <li key={d.key}>{d.label}: {d.confirmed}, {d.now}</li>)}</ul>
                </div>
              )}
              <p class="hv-modal__hint">Mit „Neu erkennen“ wird die Konfiguration erneut gelesen. Geändert wird erst, wenn du das Ergebnis übernimmst.</p>
            </section>
          )}

          {/* ------------------------------------------ 1 Set wählen */}
          {step === 'choose' && state && (
            <section class="hv-recog__panel">
              <label class="hv-modal__field">
                <span>Erkennungsset</span>
                <select value={choice} onChange={(e) => setChoice((e.target as HTMLSelectElement).value)} disabled={busy}>
                  {protocolVersions.length === 0 && state.profiles.map((p) => (
                    <option key={p.id} value={`${p.id}@${p.version}`}>{p.name} · Fassung {p.version}</option>
                  ))}
                  {[...protocolVersions].sort((a, b) => a.key.localeCompare(b.key) || b.version - a.version).map((v, i, all) => (
                    <option key={`${v.key}@${v.version}`} value={`${v.key}@${v.version}`}>
                      {v.name} · Fassung {v.version}{all.findIndex((x) => x.key === v.key) === i ? ' (neueste)' : ''}
                      {v.builtIn ? ', mitgeliefert' : ''}{v.note ? ` · ${v.note}` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {chosen && (
                <p class="hv-modal__hint">
                  {chosen.description ?? ''} Liest {chosen.probes} Konfigurationszeilen, {chosen.modules} Regeln.
                </p>
              )}
              <div class="hv-recog__info">
                <b>Was jetzt passiert:</b> Erst mit „Konfiguration lesen“ beginnt das Gerät, die Konfigurationszeilen des
                Reglers jede Minute zu lesen (nur lesen, meist nach ein bis zwei Minuten fertig, höchstens 6). Dafür legt HVACloud Lesepunkte unter „Anlagenerkennung“ an. Schema und HMI bleiben
                unverändert, bis du das Ergebnis im dritten Schritt übernimmst.
              </div>
              {state.profiles.length === 0 && (
                <p class="hv-modal__hint">Für dieses Protokoll gibt es noch kein Set. <a href="/settings/recognition">Im Editor anlegen…</a></p>
              )}
            </section>
          )}

          {/* ------------------------------------------------ 2 Lesen */}
          {step === 'reading' && state && (
            <section class="hv-recog__progress" role="status" aria-live="polite">
              <div class="hv-recog__progress-head">
                <span class="hv-recog__spinner" aria-hidden="true" />
                <b>Das Gerät liest die Konfiguration: {fresh} von {total} Zeilen</b>
                <span class="muted">noch höchstens {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}</span>
              </div>
              <div class="hv-recog__bar"><div style={{ width: `${total ? (fresh / total) * 100 : 0}%` }} /></div>
              <ul class="hv-recog__ticks">
                {probes.map((p) => (
                  <li key={p.key} class={p.fresh ? 'is-ok' : hasValue(p) ? 'is-old' : ''}
                      title={p.fresh ? 'in diesem Lauf gelesen' : hasValue(p) ? 'Wert vom letzten Lauf, wartet auf neuen' : 'wartet'}>
                    {p.fresh ? '✓' : '…'} {p.name}{hasValue(p) ? `: ${p.text ?? String(p.value)}` : ''}
                  </li>
                ))}
              </ul>
              <p class="hv-modal__hint">Blass: Wert vom letzten Lauf, der neue kommt noch. Der Regler wird nur gelesen.</p>
            </section>
          )}

          {/* -------------------------------------------- 3 Ergebnis */}
          {step === 'review' && state && (
            <>
              <div class="hv-recog__info"><b>Noch wurde nichts geändert.</b> Prüfe das Ergebnis und übernimm es, wenn es stimmt.</div>
              <div class={`hv-recog__summary${unread.length ? ' is-partial' : ''}`}>
                <b>Erkannt:</b> {found.length === 0 ? 'nur die Grundausstattung' : found.map((f) => `${f.label} (${f.text ?? f.value})`).join(', ')}.
                {absent.length > 0 && <div class="muted">Nicht vorhanden: {absent.map((f) => f.label).join(', ')}.</div>}
                {unread.length > 0 && <div>Nicht lesbar: {unread.map((f) => f.label).join(', ')}; diese Teile werden vorsichtshalber mitgenommen.</div>}
                <div class="muted">{fresh} von {total} Zeilen in diesem Lauf gelesen{fresh < total ? ', der Rest ist vom letzten Lauf oder fehlt' : ''}.</div>
              </div>

              {lost.length > 0 && (
                <div class="hv-recog__lost" role="alert">
                  <b>Fällt weg:</b> {lost.map((m) => `${m.label} (${m.leaves} Punkte)`).join(', ')}.
                  Diese Teile sind heute auf dem Gerät und in der HMI. Mit dem Übernehmen verschwinden ihre
                  Datenpunkte und Seiten. Stimmt das nicht, unten den Haken setzen.
                </div>
              )}
              <section>
                <h3 class="hv-recog__h3">Was ins Schema kommt</h3>
                <ul class="hv-recog__modules">
                  {modules.map((m) => (
                    <li key={m.key} class={included(m) ? 'is-in' : 'is-out'}>
                      <label>
                        <input type="checkbox" checked={included(m)} disabled={busy || m.rule === null}
                               onChange={(e) => setOverrides({ ...overrides, [m.key]: (e.target as HTMLInputElement).checked })} />
                        <span class="hv-recog__module-name">{m.label}</span>
                        <span class={`hv-recog__state hv-recog__state--${m.state}`}>
                          {m.rule === null ? 'immer' : m.state === 'ja' ? 'erkannt' : m.state === 'nein' ? 'nicht erkannt' : 'nicht lesbar'}
                        </span>
                        <span class="muted">{m.leaves} Punkte</span>
                      </label>
                      {m.rule && <div class="hv-recog__rule">Regel: {m.rule}</div>}
                      {overrides[m.key] !== undefined && overrides[m.key] !== m.included && (
                        <div class="hv-recog__rule"><b>von Hand {overrides[m.key] ? 'aufgenommen' : 'weggelassen'}</b></div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>

              {state.proposal && (
                <section class="hv-recog__proposal">
                  {Object.keys(overrides).length === 0 && state.proposal.match ? (
                    <p><b>Vorlage:</b> {state.proposal.match.name} (Fassung {state.proposal.match.version}) passt genau und wird angewendet.</p>
                  ) : (
                    <p><b>Vorlage:</b> {state.proposal.name}, {state.proposal.leafCount} Datenpunkte. Gibt es sie noch nicht, wird sie angelegt und lässt sich danach weiter pflegen.</p>
                  )}
                  <p class="hv-modal__hint">Datenpunkte, die bleiben, behalten ihre Kennung; HMI, Dashboards und Alarme bleiben gebunden.</p>
                  <label class="hv-recog__hmi-toggle">
                    <input type="checkbox" checked={composeHmi} onChange={(e) => setComposeHmi((e.target as HTMLInputElement).checked)} />
                    HMI des Panels an die erkannten Teile anpassen (von Hand Ergänztes bleibt)
                  </label>
                  {orphans && (
                    <div class="hv-modal__error">
                      {orphans}
                      <div><button type="button" class="hv-btn hv-btn--sm" disabled={busy} onClick={() => confirm(true)}>Trotzdem übernehmen, Bindungen gehen verloren</button></div>
                    </div>
                  )}
                </section>
              )}
              <details class="hv-recog__probes">
                <summary>Gelesene Zeilen anzeigen</summary>
                <table><tbody>
                  {probes.map((p) => (
                    <tr key={p.key} class={p.fresh ? '' : 'is-old'}>
                      <td>{p.name}</td>
                      <td class="mono">{hasValue(p) ? String(p.value) : '–'}</td>
                      <td>{p.text ?? ''}</td>
                      <td class="muted">{p.fresh ? '' : hasValue(p) ? 'letzter Lauf' : 'fehlt'}</td>
                    </tr>
                  ))}
                </tbody></table>
              </details>
            </>
          )}

          {/* ------------------------------------------------ 4 Fertig */}
          {step === 'done' && state && (
            <section class="hv-recog__panel">
              <div class="hv-recog__summary"><b>Übernommen.</b> Das Gerät hat jetzt die Datenpunkte für: {modules.filter((m) => m.included).map((m) => m.label).join(', ')}.</div>
              {state.hmiNote && <p><b>HMI:</b> {state.hmiNote}</p>}
              <p class="hv-modal__hint">Das Panel holt die neue Fassung von selbst; der Client lädt dabei einmal neu.</p>
            </section>
          )}
        </div>

        <div class="hv-modal__footer">
          <span class="hv-modal__footer-spacer" />
          {step === 'choose' && (
            <>
              <button type="button" class="hv-btn" onClick={onClose} disabled={busy}>Abbrechen</button>
              <button type="button" class="hv-btn hv-btn--primary" disabled={busy || !choice} onClick={start}>
                {busy ? 'Starte…' : 'Konfiguration lesen'}
              </button>
            </>
          )}
          {step === 'reading' && (
            <>
              <button type="button" class="hv-btn" onClick={onClose}>Schließen, läuft weiter</button>
              <button type="button" class="hv-btn" onClick={() => setStep('review')}
                      title="Mit dem weiterrechnen, was bisher da ist">Nicht warten</button>
            </>
          )}
          {step === 'review' && (
            <>
              <button type="button" class="hv-btn" onClick={() => setStep('choose')} disabled={busy}>Zurück</button>
              <button type="button" class="hv-btn hv-btn--primary" disabled={busy} onClick={() => confirm(false)}>
                {busy ? 'Übernehme…' : 'Übernehmen'}
              </button>
            </>
          )}
          {step === 'overview' && (
            <>
              <button type="button" class="hv-btn" onClick={onClose}>Schließen</button>
              <button type="button" class="hv-btn" disabled={busy} onClick={() => void rebuildHmi()}>
                {busy ? 'Setze zusammen…' : 'Nur HMI neu zusammensetzen'}
              </button>
              <button type="button" class="hv-btn hv-btn--primary" onClick={() => setStep('choose')}>Neu erkennen…</button>
            </>
          )}
          {step === 'done' && <button type="button" class="hv-btn hv-btn--primary" onClick={onClose}>Fertig</button>}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function errorText(e: unknown): string {
  if (e instanceof ApiError) return (e.body as { error?: string })?.error ?? e.message;
  return e instanceof Error ? e.message : String(e);
}
